import { GradientBlueCard } from "shared/components/gradient-blue-card/GradientBlueCard";
import "./ReportUpload.scss";
import { SectionHeading } from "shared/components/section-heading/SectionHeading";
import { SectionSubheading } from "shared/components/section-subheading/SectionSubheading";
import UploadReport from "shared/assets/images/icon-upload-report.svg";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import UploadReportSucess from "./upload-report-success/UploadReportSucess";
import UploadReportError from "./upload-report-error/UploadReportError";
import { TOKEN } from "shared/constants/StorageConsts";

const UPLOAD_API_URL =
  "https://explorer-ap-dev.strmmusic.com/api/UserArtistFile/royalty-reports/upload";
const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB em bytes
const ACCEPTED_EXTENSIONS = [".csv", ".xls", ".xlsx"];

const ReportUpload = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getFileExtension = (filename: string): string => {
    return "." + filename.split(".").pop()?.toLowerCase();
  };

  const validateFiles = (files: File[]): File[] | null => {
    for (const file of files) {
      const extension = getFileExtension(file.name);

      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        toast.error(
          `File "${file.name}" is not supported. Please upload only CSV or Excel files (.csv, .xls, .xlsx). No files were uploaded.`,
        );
        return null;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          `File "${file.name}" exceeds 30MB limit. Please select a smaller file. No files were uploaded.`,
        );
        return null;
      }
    }

    return files;
  };

  const uploadFiles = async (files: File[]) => {
    const validFiles = validateFiles(files);

    if (!validFiles) {
      return;
    }

    setSelectedFiles(validFiles);
    setIsUploading(true);

    try {
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(UPLOAD_API_URL, {
        method: "POST",
        headers: {
          accept: "text/plain",
          Authorization: `Bearer ${localStorage.getItem(TOKEN)}`,
        },
        body: formData,
      });

      if (response.ok) {
        setShowSuccess(true);
      } else {
        console.error("Upload failed:", response.status, await response.text());
        setShowError(true);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setShowError(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      uploadFiles(Array.from(files));
      event.target.value = "";
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      uploadFiles(Array.from(files));
    }
  };

  if (showSuccess) {
    return <UploadReportSucess />;
  }

  const handleCloseError = () => {
    setShowError(false);
    setSelectedFiles([]);
  };

  const handleRetry = () => {
    if (selectedFiles.length > 0) {
      setShowError(false);
      uploadFiles(selectedFiles);
    }
  };

  return (
    <section id="report-upload">
      <div className="container">
        <SectionHeading>Upload and share your reports with us</SectionHeading>
        <SectionSubheading>
          We need you to upload your reports to our team finalize the analisys
          of your case, and go further with the advance liberation
        </SectionSubheading>
        <GradientBlueCard style={{ marginTop: "88px" }}>
          <h2>How it works</h2>
          <span className="list" style={{ marginTop: "22px" }}>
            <span className="green-number-bg-circle">1</span>
            <span>
              <h2>Find and download your royalty reports</h2>
              <p>
                Need help finding your reports?{" "}
                <a href="mailto:reports@strmmusic.ai">
                  Contact our support team
                </a>
              </p>{" "}
            </span>
          </span>
          <span className="list">
            <span className="green-number-bg-circle">2</span>
            <span>
              <h2>Drag and drop your file</h2>
              <p>Upload your file and our team will be doing the analysis</p>
            </span>
          </span>
          <span className="list">
            <span className="green-number-bg-circle">3</span>
            <span>
              <h2>Wait the confirmation page</h2>
              <p>
                After the analysis, our team will update the page with a
                feedback of your report. <span className="block-in-type"></span>{" "}
                Make sure to check it out!
              </p>
            </span>
          </span>
        </GradientBlueCard>
        <GradientBlueCard style={{ marginTop: "50px" }}>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadClick}
            className={`upload-drop-zone ${isDragging ? "dragging" : ""}`}
          >
            <span className="upload-button">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
                accept=".csv,.xls,.xlsx"
                multiple
              />
              {isUploading ? (
                <div
                  className="spinner"
                  style={{
                    border: "4px solid rgba(0, 0, 0, 0.1)",
                    borderTop: "4px solid hsla(188, 100%, 43%, 1)",
                    borderRadius: "50%",
                    width: "60px",
                    height: "60px",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                <img
                  src={UploadReport}
                  alt="Upload Report"
                  style={{ cursor: "pointer" }}
                />
              )}
            </span>
            <h2 style={{ textAlign: "center", marginTop: "26px" }}>
              Upload your report
            </h2>
            <p style={{ textAlign: "center", marginTop: "16px" }}>
              Drag and drop your files or{" "}
              <span
                style={{ color: "hsla(188, 100%, 43%, 1)", cursor: "pointer" }}
              >
                Browse
              </span>
            </p>
            <p className="accepted-file-name">
              Supports CSV, XLS, and XLSX (Max 30MB)
            </p>
            {selectedFiles.length > 0 && (
              <p className="selected-file-name">
                Selected files: {selectedFiles.map((f) => f.name).join(", ")}
              </p>
            )}
          </div>
        </GradientBlueCard>
      </div>
      {showError && (
        <UploadReportError
          fileName={selectedFiles.map((f) => f.name).join(", ")}
          fileCount={selectedFiles.length}
          onClose={handleCloseError}
          onRetry={handleRetry}
        />
      )}
    </section>
  );
};

export default ReportUpload;
